import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
import { getEmployeeReport, exportEmployeeReportToExcel } from "../../components/Api/GmsEmployeeReport";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getDepartments } from "../../components/Api/GmsDepartment";
import { getDesignations } from "../../components/Api/GmsDesignation";
import { getBranches } from "../../components/Api/GmsBranch";
import { getCountries } from "../../components/Api/GmsCountry";
import { searchEmployees } from "../../components/Api/GmsEmployee";
import { Filter, Download, Eye } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const emptyFilter = {
  emp_No: "",
  employee_NameEn: "",
  business_Id: null,
  department_Id: null,
  designation_Id: null,
  branch_Id: null,
  country_Id: null,
  onWork: null,
};

export default function EmployeeReportPage() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [employeeNameSearchOptions, setEmployeeNameSearchOptions] = useState([]);
  const [employeeNameSearchLoading, setEmployeeNameSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsEmployeeReport');
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate business if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !filter.business_Id) {
      setFilter(prev => ({ ...prev, business_Id: session.businessId }));
      // Load branches for the auto-populated business
      loadBranches(session.businessId);
    }
  }, [businesses, session.businessId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && filter.business_Id === session.businessId && !filter.branch_Id) {
      setFilter(prev => ({ ...prev, branch_Id: session.branchId }));
    }
  }, [branches, session.branchId, filter.business_Id]);

  useEffect(() => {
    if (businesses.length > 0) {
      loadEmployees();
    }
  }, [businesses]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadBusinesses(),
        loadDepartments(),
        loadDesignations(),
        loadBranches(), // Load all branches initially, will be filtered when business is selected
        loadCountries(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({});
      setBusinesses(data || []);
    } catch (error) {
      console.error("Error loading businesses:", error);
      setBusinesses([]);
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await getDepartments({});
      setDepartments(data || []);
    } catch (error) {
      console.error("Error loading departments:", error);
      setDepartments([]);
    }
  };

  const loadDesignations = async () => {
    try {
      const data = await getDesignations({});
      setDesignations(data || []);
    } catch (error) {
      console.error("Error loading designations:", error);
      setDesignations([]);
    }
  };

  const loadBranches = async (businessId = null) => {
    try {
      const filter = businessId ? { Branch_Buisness_Id: businessId } : {};
      const data = await getBranches(filter);
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  const loadCountries = async () => {
    try {
      const data = await getCountries();
      setCountries(data || []);
    } catch (error) {
      console.error("Error loading countries:", error);
      setCountries([]);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const filterData = {
        emp_No: filter.emp_No,
        employee_NameEn: filter.employee_NameEn,
        business_Id: filter.business_Id || session.businessId,
        department_Id: filter.department_Id,
        designation_Id: filter.designation_Id,
        branch_Id: filter.branch_Id || (session.branchId && session.businessId ? session.branchId : null),
        country_Id: filter.country_Id,
        onWork: filter.onWork
      };

      const data = await getEmployeeReport(filterData);
      setEmployees(data || []);

      // Load all employees for dropdown options (filtered by session if available)
      if (allEmployees.length === 0) {
        const allDataFilter = {};
        if (session.businessId) {
          allDataFilter.business_Id = session.businessId;
        }
        if (session.branchId && session.businessId) {
          allDataFilter.branch_Id = session.branchId;
        }
        const allData = await getEmployeeReport(allDataFilter);
        setAllEmployees(allData || []);
      }
    } catch (error) {
      console.error("Error loading employees:", error);
      Swal.fire("Error", "Failed to load employee report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilter({ ...filter, [e.target.name]: e.target.value });
  };

  // Manual search function for employee name filter search
  const searchEmployeeNamesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeNameSearchOptions([]);
      return;
    }

    try {
      setEmployeeNameSearchLoading(true);
      // Pass the selected company and branch from filter
      const businessId = filter.business_Id || session.businessId || null;
      const branchId = filter.branch_Id || (session.branchId && session.businessId ? session.branchId : null);
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_NameEn,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
        employee: employee
      }));

      setEmployeeNameSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeNameSearchOptions([]);
    } finally {
      setEmployeeNameSearchLoading(false);
    }
  };

  const handleSelectChange = async (selectedOption, field) => {
    const value = selectedOption?.value || null;

    // If business filter changes, reset branch filter and load branches for that business
    if (field === 'business_Id') {
      setFilter({
        ...filter,
        [field]: value,
        branch_Id: null, // Reset branch selection when business changes
        employee_NameEn: null // Clear employee name when business changes
      });
      setEmployeeNameSearchOptions([]);

      // Load branches for selected business
      if (value) {
        await loadBranches(value);
      } else {
        await loadBranches(); // Load all branches if no business selected
      }
    } else if (field === 'branch_Id') {
      setFilter({
        ...filter,
        [field]: value,
        employee_NameEn: null // Clear employee name when branch changes
      });
      setEmployeeNameSearchOptions([]);
    } else {
      setFilter({ ...filter, [field]: value });
    }
  };

  const handleApplyFilter = () => {
    loadEmployees();
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setEmployeeNameSearchOptions([]);
    loadEmployees();
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const filterData = {
        emp_No: filter.emp_No,
        employee_NameEn: filter.employee_NameEn,
        business_Id: filter.business_Id,
        department_Id: filter.department_Id,
        designation_Id: filter.designation_Id,
        branch_Id: filter.branch_Id,
        country_Id: filter.country_Id,
        onWork: filter.onWork
      };

      await exportEmployeeReportToExcel(filterData);
      Swal.fire("Success", "Employee report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting report:", error);
      Swal.fire("Error", "Failed to export employee report", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount || 0);
  };

  // Generate options for dropdowns
  const employeeCodeOptions = [...new Set(allEmployees.map(emp => emp.emp_No).filter(Boolean))].map(code => ({
    value: code,
    label: code
  }));

  const employeeNameOptions = [...new Set(allEmployees.map(emp => emp.employee_NameEn).filter(Boolean))].map(name => ({
    value: name,
    label: name
  }));

  const businessOptions = businesses.map(business => ({
    value: business.business_Id,
    label: `${business.business_NameEn} - ${business.business_NameAr}`
  }));

  const departmentOptions = departments.map(dept => ({
    value: dept.department_Id,
    label: `${dept.department_NameEn} - ${dept.department_NameAr}`
  }));

  const designationOptions = designations.map(des => ({
    value: des.designation_Id,
    label: `${des.designation_NameEn} - ${des.designation_NameAr}`
  }));

  // Filter branch options based on selected business
  const branchOptions = branches
    .filter(branch => !filter.business_Id || branch.branch_Buisness_Id === filter.business_Id)
    .map(branch => ({
      value: branch.branch_Id,
      label: `${branch.branch_NameEn} - ${branch.branch_NameAr}`
    }));

  const countryOptions = countries.map(country => ({
    value: country.country_Id,
    label: `${country.country_NameEn} - ${country.country_NameAr}`
  }));

  const workStatusOptions = [
    { value: true, label: 'Working' },
    { value: false, label: 'Not Working' },
    { value: null, label: 'All' }
  ];

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Employee Report" breadcrumbItem="Reports" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0">Employee Report</h4>
                  <div>
                    <Button
                      color="primary"
                      className="me-2"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Filter className="me-1" size={16} />
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                    <Button
                      color="success"
                      onClick={handleExportExcel}
                      disabled={loading}
                    >
                      <Download className="me-1" size={16} />
                      Export Excel
                    </Button>
                  </div>
                </div>

                {/* Filters Section */}
                {showFilters && (
                  <Card className="mb-4">
                    <CardBody>
                      <Row>
                        <Col md="3">
                          <Label>Employee Code</Label>
                          <Select
                            options={employeeCodeOptions}
                            value={employeeCodeOptions.find(opt => opt.value === filter.emp_No)}
                            onChange={(option) => handleSelectChange(option, 'emp_No')}
                            isClearable
                            isSearchable
                            placeholder="Select Employee Code"
                            styles={selectStyles}
                          />
                        </Col>
                        <Col md="3">
                          <Label>Employee Name</Label>
                          <Select
                            options={employeeNameSearchOptions}
                            value={filter.employee_NameEn ? employeeNameSearchOptions.find(opt => opt.value === filter.employee_NameEn) : null}
                            placeholder="Type at least 3 characters to search..."
                            classNamePrefix="select2-selection"
                            isClearable
                            isSearchable
                            isLoading={employeeNameSearchLoading}
                            filterOption={() => true}
                            noOptionsMessage={({ inputValue }) => {
                              if (employeeNameSearchLoading) return "Searching...";
                              if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                              return "No employees found";
                            }}
                            loadingMessage={() => "Searching employees..."}
                            onInputChange={(inputValue, { action }) => {
                              if (action === 'input-change') {
                                if (searchTimeout) {
                                  clearTimeout(searchTimeout);
                                }
                                const timeout = setTimeout(() => {
                                  searchEmployeeNamesManually(inputValue);
                                }, 300);
                                setSearchTimeout(timeout);
                              }
                              return inputValue;
                            }}
                            onChange={(option) => {
                              handleSelectChange(option, 'employee_NameEn');
                              if (option) {
                                setEmployeeNameSearchOptions([]);
                              }
                            }}
                            styles={selectStyles}
                          />
                        </Col>
                        <Col md="3">
                          <Label>Company</Label>
                          <Select
                            options={businessOptions}
                            value={businessOptions.find(opt => opt.value === filter.business_Id)}
                            onChange={(option) => handleSelectChange(option, 'business_Id')}
                            isClearable={!session.businessId}
                            isDisabled={!!session.businessId}
                            isSearchable
                            placeholder="Select Company"
                            styles={selectStyles}
                          />
                        </Col>
                        <Col md="3">
                          <Label>Department</Label>
                          <Select
                            options={departmentOptions}
                            value={departmentOptions.find(opt => opt.value === filter.department_Id)}
                            onChange={(option) => handleSelectChange(option, 'department_Id')}
                            isClearable
                            isSearchable
                            placeholder="Select Department"
                            styles={selectStyles}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-3">
                        <Col md="3">
                          <Label>Designation</Label>
                          <Select
                            options={designationOptions}
                            value={designationOptions.find(opt => opt.value === filter.designation_Id)}
                            onChange={(option) => handleSelectChange(option, 'designation_Id')}
                            isClearable
                            isSearchable
                            placeholder="Select Designation"
                            styles={selectStyles}
                          />
                        </Col>
                        <Col md="3">
                          <Label>Branch</Label>
                          <Select
                            options={branchOptions}
                            value={branchOptions.find(opt => opt.value === filter.branch_Id)}
                            onChange={(option) => handleSelectChange(option, 'branch_Id')}
                            isClearable={!session.branchId}
                            isDisabled={!!session.branchId || !filter.business_Id}
                            isSearchable
                            placeholder="Select Branch"
                            styles={selectStyles}
                          />
                        </Col>
                        <Col md="3">
                          <Label>Country</Label>
                          <Select
                            options={countryOptions}
                            value={countryOptions.find(opt => opt.value === filter.country_Id)}
                            onChange={(option) => handleSelectChange(option, 'country_Id')}
                            isClearable
                            isSearchable
                            placeholder="Select Country"
                            styles={selectStyles}
                          />
                        </Col>
                        <Col md="3">
                          <Label>Work Status</Label>
                          <Select
                            options={workStatusOptions}
                            value={workStatusOptions.find(opt => opt.value === filter.onWork)}
                            onChange={(option) => handleSelectChange(option, 'onWork')}
                            isClearable
                            isSearchable
                            placeholder="Select Work Status"
                            styles={selectStyles}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-3">
                        <Col md="12">
                          <Button color="primary" onClick={handleApplyFilter} className="me-2">
                            Apply Filter
                          </Button>
                          <Button color="secondary" onClick={handleClearFilter}>
                            Clear Filter
                          </Button>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Data Table */}
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center py-4">
                      <ClipLoader color="#556ee6" loading={loading} size={35} />
                      <p className="mt-2">Loading employee report...</p>
                    </div>
                  ) : (
                    <Table className="table table-striped table-bordered">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Code</th>
                          <th>Emp No</th>
                          <th>Name (EN)</th>
                          <th>Company</th>
                          <th>Department</th>
                          <th>Designation</th>
                          <th>Branch</th>
                          <th>Country</th>
                          <th>Gross Salary</th>
                          <th>Work Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employees.length > 0 ? (
                          employees.map((employee, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{employee.employee_Code}</td>
                              <td>{employee.emp_No}</td>
                              <td>{employee.employee_NameEn}</td>
                              <td>{employee.company}</td>
                              <td>{employee.department}</td>
                              <td>{employee.designation}</td>
                              <td>{employee.branch}</td>
                              <td>{employee.country}</td>
                              <td>{formatCurrency(employee.grossSalary)}</td>
                              <td>
                                <span className={`badge ${employee.workStatus === 'Working' ? 'bg-success' : 'bg-danger'}`}>
                                  {employee.workStatus}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="11" className="text-center">No data found</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
